import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import { Loading } from '../components/Loading'
import { StatusBadge } from '../components/StatusBadge'
import {
  formatArxivIdentifier,
  formatBytes,
  formatDate,
  getArxivAbstractUrl,
  getArxivPdfUrl,
} from '../lib/utils'
import type { Submission, SubmissionStatus } from '../types'

const FINAL_OPTIONS: Array<{
  value: Extract<SubmissionStatus, 'completed' | 'unreadable' | 'declined_conflict' | 'rejected_security'>
  label: string
  kind: 'feedback' | 'notice'
}> = [
  { value: 'completed', label: 'Publish normal feedback', kind: 'feedback' },
  { value: 'unreadable', label: 'Close: manuscript could not be opened', kind: 'notice' },
  {
    value: 'declined_conflict',
    label: 'Close: substantial overlap with current research',
    kind: 'notice',
  },
  { value: 'rejected_security', label: 'Close: security problem', kind: 'notice' },
]

export function AdminPage() {
  const [items, setItems] = useState<Submission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [feedback, setFeedback] = useState('')
  const [finalStatus, setFinalStatus] = useState<(typeof FINAL_OPTIONS)[number]['value']>(
    'completed',
  )
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const { data, error: queryError } = await supabase
      .from('submissions')
      .select('*')
      .in('status', ['queued', 'in_progress'])
      .order('submitted_at', { ascending: true })

    if (queryError) setError(queryError.message)
    else {
      setItems((data ?? []) as Submission[])
      setError(null)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const current = useMemo(
    () => items.find((item) => item.status === 'in_progress') ?? null,
    [items],
  )

  const currentSource = current?.source_type ?? 'pdf'

  const startNext = async () => {
    setBusy(true)
    setError(null)
    const { error: rpcError } = await supabase.rpc('start_next_submission')
    if (rpcError) setError(rpcError.message)
    await load()
    setBusy(false)
  }

  const openManuscript = async () => {
    if (!current) return

    if (currentSource === 'arxiv') {
      if (!current.arxiv_id) {
        setError('The arXiv identifier is missing.')
        return
      }
      window.open(getArxivPdfUrl(current.arxiv_id), '_blank', 'noopener,noreferrer')
      return
    }

    if (!current.storage_path) {
      setError('The private PDF is no longer available.')
      return
    }

    const { data, error: signedUrlError } = await supabase.storage
      .from('manuscripts')
      .createSignedUrl(current.storage_path, 300)

    if (signedUrlError) {
      setError(signedUrlError.message)
      return
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
  }

  const publish = async () => {
    if (!current) return
    if (!feedback.trim()) {
      setError('Write the private feedback or service notice before closing the item.')
      return
    }

    const selected = FINAL_OPTIONS.find((option) => option.value === finalStatus)
    if (!selected) return

    const confirmed = window.confirm(
      currentSource === 'pdf'
        ? 'Publish this private text and permanently delete the source PDF from active storage?'
        : 'Publish this private text and close the arXiv-based submission? No manuscript copy is stored by Rapid First Look.',
    )
    if (!confirmed) return

    setBusy(true)
    setError(null)
    const { error: functionError } = await supabase.functions.invoke('publish-feedback', {
      body: {
        submissionId: current.id,
        body: feedback.trim(),
        finalStatus,
        kind: selected.kind,
      },
    })

    if (functionError) {
      setError(functionError.message)
      setBusy(false)
      return
    }

    setFeedback('')
    setFinalStatus('completed')
    await load()
    setBusy(false)
  }

  return (
    <section className="section">
      <div className="container">
        {error && <div className="notice notice-error">{error}</div>}

        {loading ? (
          <Loading label="Loading admin queue..." />
        ) : (
          <div className="admin-grid">
            <div className="panel">
              <div className="panel-header">
                <div>
                  <h2>Active queue</h2>
                  <p>{items.length} active item(s)</p>
                </div>
                {!current && items.length > 0 && (
                  <button
                    className="button button-primary button-small"
                    type="button"
                    disabled={busy}
                    onClick={() => void startNext()}
                  >
                    Start next manuscript
                  </button>
                )}
              </div>

              {items.length === 0 ? (
                <p className="empty-state">No manuscripts are waiting.</p>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Submitted</th>
                        <th>Source</th>
                        <th>Details</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => {
                        const source = item.source_type ?? 'pdf'
                        return (
                          <tr key={item.id} className={item.id === current?.id ? 'active-row' : ''}>
                            <td className="mono">{item.public_code}</td>
                            <td>{formatDate(item.submitted_at)}</td>
                            <td>{source === 'arxiv' ? 'arXiv' : 'Private PDF'}</td>
                            <td>
                              {source === 'arxiv' && item.arxiv_id
                                ? formatArxivIdentifier(item.arxiv_id)
                                : formatBytes(item.file_size_bytes)}
                            </td>
                            <td>
                              <StatusBadge status={item.status} />
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <aside className="panel admin-workbench">
              <h2>Current first look</h2>
              {!current ? (
                <p className="empty-state">
                  Start the next manuscript to open the private feedback workbench.
                </p>
              ) : (
                <>
                  <div className="workbench-meta">
                    <span className="mono">{current.public_code}</span>
                    <StatusBadge status={current.status} />
                  </div>
                  <dl className="detail-grid">
                    <div>
                      <dt>Submitted</dt>
                      <dd>{formatDate(current.submitted_at)}</dd>
                    </div>
                    <div>
                      <dt>Source</dt>
                      <dd>{currentSource === 'arxiv' ? 'arXiv' : 'Private PDF'}</dd>
                    </div>
                    <div>
                      <dt>{currentSource === 'arxiv' ? 'Identifier' : 'PDF size'}</dt>
                      <dd>
                        {currentSource === 'arxiv' && current.arxiv_id ? (
                          <a
                            href={getArxivAbstractUrl(current.arxiv_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {formatArxivIdentifier(current.arxiv_id)}
                          </a>
                        ) : (
                          formatBytes(current.file_size_bytes)
                        )}
                      </dd>
                    </div>
                  </dl>

                  <button
                    className="button button-secondary full-width"
                    type="button"
                    onClick={() => void openManuscript()}
                  >
                    {currentSource === 'arxiv'
                      ? 'Open latest version on arXiv'
                      : 'Open private PDF for 5 minutes'}
                  </button>

                  <label htmlFor="feedback">Private text for the author</label>
                  <textarea
                    id="feedback"
                    rows={16}
                    value={feedback}
                    onChange={(event) => setFeedback(event.target.value)}
                    placeholder="Write freely: what stood out, what worked, what did not, and any useful suggestions..."
                  />

                  <label htmlFor="final-status">How should this item be closed?</label>
                  <select
                    id="final-status"
                    value={finalStatus}
                    onChange={(event) =>
                      setFinalStatus(event.target.value as typeof finalStatus)
                    }
                  >
                    {FINAL_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <button
                    className="button button-primary full-width"
                    type="button"
                    disabled={busy || !feedback.trim()}
                    onClick={() => void publish()}
                  >
                    {busy
                      ? 'Closing item...'
                      : currentSource === 'pdf'
                        ? 'Publish privately and delete source PDF'
                        : 'Publish private feedback'}
                  </button>
                </>
              )}
            </aside>
          </div>
        )}
      </div>
    </section>
  )
}
